import type { ComponentType } from 'react';
import type { Property } from '@vibesites/schema';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  useProperty,
  useRegistry,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { PropertyInfoPanel, addressGroupDef } from './PropertyInfoPanel';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const ELISE_TOUR_URL_FIELD = 'custom_eliseai_tour_scheduler_url';
/** ApartmentSync-backed tour form. Used when the property has no Elise scheduler. */
const DEFAULT_APTSYNC_BLOCK_ID = 'multihub-tour-scheduler';
const IFRAME_BLOCK_IDS = new Set(['elise-schedule-tour-iframe', 'iframe-embed', 'virtual-tour']);
const EMPTY_SCHEDULER_IDS = new Set([
  '',
  'null',
  'undefined',
  'none',
  'n/a',
  'na',
  '-',
  '0',
  'false',
  'true',
]);

function normalizeFieldId(id: string | undefined | null): string {
  return (id ?? '').trim().toLowerCase();
}

function normalizeFieldValue(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || EMPTY_SCHEDULER_IDS.has(trimmed.toLowerCase())) return null;
    return trimmed;
  }
  if (value && typeof value === 'object' && 'value' in value) {
    return normalizeFieldValue((value as { value: unknown }).value);
  }
  return null;
}

/**
 * Resolve a custom property field from the flattened key (deployed sites)
 * or the custom_fields array (editor preview).
 */
function resolveCustomFieldValue(property: Property, fieldId: string): string | null {
  const normalizedId = normalizeFieldId(fieldId);

  for (const [key, value] of Object.entries(property)) {
    if (normalizeFieldId(key) !== normalizedId) continue;
    const resolved = normalizeFieldValue(value);
    if (resolved) return resolved;
  }

  for (const cf of property.custom_fields ?? []) {
    const id = cf.ID ?? (cf as { id?: string }).id ?? '';
    if (normalizeFieldId(id) !== normalizedId) continue;
    const resolved = normalizeFieldValue(cf.value);
    if (resolved) return resolved;
  }

  return null;
}

/**
 * Elise embeds only when the scheduler URL includes a real id.
 * A bare host, an empty `?id=`, or `…/tour` with no building slug is absent.
 */
function eliseSchedulerUrl(raw: string | null): string | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

  for (const key of ['id', 'building', 'buildingId', 'building_id', 'schedulerId', 'calendarId']) {
    if (!url.searchParams.has(key)) continue;
    const id = url.searchParams.get(key)?.trim() ?? '';
    if (!id || EMPTY_SCHEDULER_IDS.has(id.toLowerCase())) return null;
    return url.toString();
  }

  const segments = url.pathname.split('/').filter(Boolean);
  if (segments.length === 0) return null;
  const last = segments[segments.length - 1].toLowerCase();
  if ((last === 'tour' || last === 'contactus') && segments.length < 3) return null;
  return url.toString();
}

interface FormSlot {
  BlockComponent: ComponentType<{ sectionId: string }>;
  blockId: string;
  sectionId: string;
}

function aptSyncFormSlot(
  registry: { [slot: string]: unknown },
  formSlot: FormSlot | null,
  formBlockPath: string,
): FormSlot | null {
  if (formSlot && !IFRAME_BLOCK_IDS.has(formSlot.blockId)) return formSlot;

  const BlockComponent = registry[`block:${DEFAULT_APTSYNC_BLOCK_ID}`] as
    | ComponentType<{ sectionId: string }>
    | null
    | undefined;
  if (!BlockComponent) return formSlot;

  return {
    BlockComponent,
    blockId: DEFAULT_APTSYNC_BLOCK_ID,
    sectionId: formBlockPath.replace(/_block$/, ''),
  };
}

// ============================================================================
// Component
// ============================================================================

export function ScheduleTourForm() {
  const prefix = 'scheduletourform';
  const formBlockPath = `${prefix}.form_block`;
  const property = useProperty();
  const registry = useRegistry();

  const eliseTourSchedulerUrl = eliseSchedulerUrl(
    resolveCustomFieldValue(property, ELISE_TOUR_URL_FIELD),
  );

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: DEFAULT_APTSYNC_BLOCK_ID,
  });
  const aptSyncSlot = aptSyncFormSlot(registry, formSlot, formBlockPath);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="cf-section stf-section">
      <div ref={sectionRef} className="reveal-fade-up cf-container">
        <div className="cf-grid">
          {/* Form first in the DOM so it stacks above contact on mobile/tablet. */}
          <div className="cf-form-col">
            {eliseTourSchedulerUrl ? (
              <div className="cf-form-card cf-elise-embed">
                <iframe
                  src={eliseTourSchedulerUrl}
                  title="Schedule a Tour"
                  width="100%"
                  height="800"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <EditableToken path={formBlockPath} as="div">
                {aptSyncSlot ? (
                  <SectionContextProvider sectionId={aptSyncSlot.sectionId}>
                    <aptSyncSlot.BlockComponent sectionId={aptSyncSlot.sectionId} />
                  </SectionContextProvider>
                ) : (
                  <div className="cf-form-card">
                    <p className="cf-form-empty">No form block selected</p>
                  </div>
                )}
              </EditableToken>
            )}
          </div>

          {/* Contact info: below the form when stacked; left column on desktop. */}
          <div className="cf-info-col">
            <PropertyInfoPanel prefix={prefix} />
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — static paths so the linter can match each schema key.
          The live EditableTokens in PropertyInfoPanel use dynamic paths from prefix. */}
      <EditableToken path="scheduletourform.form_block"   as="div"  className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.visit_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableSlotGroup groupPath="scheduletourform.address" definition={addressGroupDef} className="hidden" style={{ display: 'none' }}><span /></EditableSlotGroup>
      <EditableToken path="scheduletourform.call_label"   as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.phone"        as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.hours_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        .cf-section {
          padding: 2.5rem 0 3.5rem;
          background: white;
        }
        @media (min-width: 768px) {
          .cf-section { padding: 3rem 0 4rem; }
        }
        .cf-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px)  { .cf-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .cf-container { padding: 0 2rem; } }

        .stf-section .cf-grid {
          display: grid;
          grid-template-columns: 1fr;
          grid-template-areas: "form" "info";
          gap: 2rem;
        }
        @media (min-width: 1024px) {
          .stf-section .cf-grid {
            grid-template-columns: 5fr 7fr;
            grid-template-areas: "info form";
            gap: 2.5rem;
            align-items: start;
          }
        }
        .stf-section .cf-form-col { grid-area: form; }
        .stf-section .cf-info-col { grid-area: info; }

        /* ===== Form column ===== */
        .cf-form-col { position: relative; }
        .cf-form-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          padding: 2rem;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }
        .cf-elise-embed {
          padding: 0;
          overflow: hidden;
        }
        .cf-elise-embed iframe {
          display: block;
          width: 100%;
          border: none;
          min-height: 800px;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleTourForm',
  displayName: 'Schedule A Tour Form',
  tokens: {
    'scheduletourform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
      description:
        'ApartmentSync tour form. Shown when the property has no EliseAI tour scheduler id.',
    },
    'scheduletourform.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'scheduletourform.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'scheduletourform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      defaultValue: '(555) 555-5555',
      description: 'Call tracking number, then the property phone, then this placeholder.',
    },
    'scheduletourform.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'scheduletourform.address': {
      label: 'Address',
      fields: {
        text: {
          label: 'Address Text',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '',
        },
        url: {
          label: 'Map URL',
          type: 'url',
          // URL has no defaultField — defaults to usePropertyAddressUrl() at render
          // time. Editor overrides still win.
          defaultValue: '',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_blank',
        },
      },
    },
  },
  collectionBindings: {
    'scheduletourform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ScheduleTourForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleTourForm;
